// Project records for the All Work timeline. Only confirmed information
// is included: dates, roles, descriptions, skills, outcomes and media
// exactly as supplied. Media supports YouTube, Instagram and editorial
// links, grouped per project.

const YT = 'youtube';
const IG = 'instagram';
const ED = 'editorial';

export const PROJECTS = [
  // ─── MARKET 01 — NORTH AMERICA ───────────────────────────────
  {
    id: 'pantone',
    cluster: 'na',
    brand: 'Pantone',
    title: 'MagentaVerse — Color of the Year 2023',
    date: 'Nov 2022 — Dec 2022',
    location: 'United States',
    role: 'Creative Director / Producer',
    description:
      'Worked on the MagentaVerse campaign for Pantone Color of the Year 2023. Produced and directed the hero film, along with influencer and art-creator campaign amplification across Instagram.',
    skills: ['Creative Direction', 'Video Production', 'Creative Production', 'Influencer Campaign Strategy', 'Campaign Development', 'Generative AI Collaboration'],
    mediaGroups: [
      { label: 'Hero film', items: [{ type: YT, url: 'https://youtu.be/LpptWoJ3okQ' }] },
      {
        label: 'Influencer and art-creator amplification',
        items: [
          { type: IG, url: 'https://www.instagram.com/reels/ClpRhabOka3/' },
          { type: IG, url: 'https://www.instagram.com/reels/CnA6hd1Dtao/' },
          { type: IG, url: 'https://www.instagram.com/p/Cp_vETiDAW_/' },
        ],
      },
    ],
  },
  {
    id: 'vv',
    cluster: 'na',
    brand: 'Value Village',
    date: 'Jan 2023 — Dec 2023',
    location: 'Canada',
    role: 'Content Strategist, Retail Social Media',
    description:
      'Led the redesign of Value Village retail social media strategy, moving the brand to community-led content, user-generated content, influencer collaborations, seasonal storytelling and store-level marketing. Grew UGC-led publishing from approximately 1–2 posts per month to 30–40 posts per month alongside the branded calendar, and built recurring series around tentpole moments: back-to-school looks, teacher outfits and Thrift Flips, a Reels series on customer restyling. Developed influencer-led activations and launch communication for new store openings.',
    skills: ['Retail Social Media Strategy', 'Social Media Strategy Redesign', 'User-Generated Content Strategy', 'Community-Led Content Development', 'Influencer Marketing', 'Store-Opening Communication', 'Retail Content Strategy', 'Instagram Reels Strategy', 'Seasonal Campaign Planning', 'Content Calendar Development', 'Retail Storytelling'],
    outcomes: [
      'UGC-led publishing grown from ~1–2 to 30–40 posts per month, on top of the branded calendar',
      'Approximately 3%–3.5% rise in social media engagement',
      '25% increase in store footfall after an influencer collaboration for a new store, measured by in-store entrance sensors',
    ],
    mediaGroups: [
      { label: 'Influencer collaboration — primary retail activation', items: [{ type: IG, url: 'https://www.instagram.com/p/CvIfmnwouND/' }] },
      {
        label: 'Supporting content',
        items: [
          { type: IG, url: 'https://www.instagram.com/p/CwnI2K2L8Me/' },
          { type: IG, url: 'https://www.instagram.com/p/CxnguPEtumU/' },
        ],
      },
    ],
  },
  {
    id: 'td',
    cluster: 'na',
    brand: 'TD Bank',
    title: '#TDUnionSummer',
    date: 'May 2023 — Jul 2023',
    location: 'Canada',
    role: 'Content Strategist, Events',
    description:
      'Developed content for a high-engagement, co-branded public activation at Toronto Union Station with TD Bank: a video series highlighting artists and performers curated by TD at one of Canada largest commuter hubs, distributed across social media and out-of-home placements. The activation engaged an environment of more than 50,000 daily commuters and continued as an annual #TDUnionSummer event.',
    skills: ['Content Strategy', 'Event Content Strategy', 'Event Curation', 'Creative Partnerships', 'Video Content Development', 'Social Media Content', 'Out-of-Home Content Adaptation'],
    mediaGroups: [
      {
        label: 'Campaign content',
        items: [
          { type: IG, url: 'https://www.instagram.com/p/CuzPUAYu1L9/' },
          { type: IG, url: 'https://www.instagram.com/p/CvD5hQfvloN/' },
          { type: IG, url: 'https://www.instagram.com/p/CuMxqZEuVW7/' },
        ],
      },
    ],
  },
  {
    id: 'goose',
    cluster: 'na',
    brand: 'Canada Goose',
    title: 'Stitched in Ages',
    date: 'Jun 2023',
    location: 'Canada',
    role: 'Content Strategist',
    description:
      'Developed content creation and strategy for Stitched in Ages, a sustainability-focused campaign with creative partner GANGBOX. The campaign explored the relationship between humans and nature through upcycled brand materials: a physical art installation of figures made from upcycled materials communicating the importance of human impact on the environment.',
    skills: ['Content Strategy', 'Creative Strategy', 'Sustainability Storytelling', 'Art Direction', 'Campaign Content Development', 'Creative Partnerships'],
    notes: ['Campaign screenshots to be supplied — some original social posts are no longer available.'],
    mediaGroups: [
      {
        label: 'Campaign content and coverage',
        items: [
          { type: IG, url: 'https://www.instagram.com/p/CuPsRspgIXE/?img_index=6' },
          { type: ED, url: 'https://www.auburnlane.com/gangbox-partners-with-canada-goose-in-a-new-art-installation/', label: 'Auburn Lane — editorial coverage' },
        ],
      },
    ],
  },

  // ─── MARKET 02 — UAE (Dubai) ─────────────────────────────────
  {
    id: 'haagendazs',
    cluster: 'uae',
    brand: 'Häagen-Dazs',
    date: 'Jan 2018',
    location: 'Dubai, UAE',
    role: 'Creative Producer',
    description:
      'Produced a brand film showcasing a newly opened Häagen-Dazs retail outlet in Dubai. Responsible for bringing the production team together, including onboarding the videographers and photographers required for the project.',
    skills: ['Creative Production', 'Video Production', 'Photography Production', 'Production Team Coordination', 'Brand Film Production', 'Creative Execution'],
    notes: ['No campaign link is currently available.'],
  },
  {
    id: 'careem',
    cluster: 'uae',
    brand: 'Careem',
    date: 'Mar 2018',
    location: 'Dubai, UAE',
    role: 'Content Strategist',
    description:
      'Worked on content strategy for the launch of Careem in the Dubai market. The campaign pitch was approved and executed.',
    skills: ['Content Strategy', 'Brand Communication', 'Campaign Strategy', 'Market Launch Communication'],
    notes: ['The approved campaign concept and supporting materials are confidential and are not displayed. No public campaign links are available.'],
  },

  // ─── MARKET 03 — INDIA ───────────────────────────────────────
  {
    id: 'govdoc',
    cluster: 'ind',
    group: 'freelance',
    brand: 'Government of India',
    title: 'Clean India Initiative Documentary',
    date: 'May 2017',
    location: 'India',
    role: 'Producer and Post-Production Lead',
    agency: 'Quality Council of India (QCI)',
    description:
      'Produced and managed post-production for a documentary supporting the Clean India Initiative, showcasing findings from the Swachh Survekshan survey and documenting behavioural change across Indian villages through interviews, data visualisation and on-ground footage. Coordinated video production, worked with rural government officials, managed post-production workflows and edited the film to national broadcast standards. Filmed across 25+ cities and aired on national television.',
    skills: ['Documentary Production', 'Field Production', 'Production Coordination', 'Post-Production Management', 'Video Editing', 'Government and Stakeholder Coordination', 'Data-Led Storytelling'],
    mediaGroups: [{ label: 'Documentary film', items: [{ type: YT, url: 'https://www.youtube.com/watch?v=BvO7rfHZqtI' }] }],
  },
  {
    id: 'della',
    cluster: 'ind',
    group: 'freelance',
    brand: 'Della Adventures and Resorts',
    date: 'Jul 2017 — Oct 2017',
    location: 'India',
    role: 'Content Lead',
    description:
      'Managed a four-member creative team developing immersive video and editorial content showcasing resort experiences and adventure activities for corporate professionals and travellers. Created the recurring A Day At video series and other experience-led content.',
    skills: ['Content Leadership', 'Team Management', 'Video Production', 'Travel Content', 'Experiential Storytelling', 'Editorial Content', 'Creative Direction'],
    mediaGroups: [
      { label: 'A Day At series', items: [{ type: YT, url: 'https://www.youtube.com/watch?v=OSxMUvrcUaQ' }, { type: YT, url: 'https://www.youtube.com/watch?v=-NqrFQ9VWMA' }] },
      { label: 'Additional films', items: [{ type: YT, url: 'https://www.youtube.com/watch?v=4Fre6WzC2xs' }, { type: YT, url: 'https://www.youtube.com/watch?v=4TMMc2op6Jw' }] },
      { label: 'Instagram', items: [{ type: IG, url: 'https://www.instagram.com/p/BZ01HMZAms0/' }, { type: IG, url: 'https://www.instagram.com/dellaresortsandadventure/reel/BavgGLdgc31/' }] },
    ],
  },
  {
    id: 'epified',
    cluster: 'ind',
    group: 'culturemachine',
    brand: 'Epified',
    date: 'Date not specified',
    location: 'India',
    role: 'Video Producer',
    description:
      'Scripted, wrote and provided art direction for mythology and Indian philosophy-led video content, drawing on a deep interest in Indian mythology, folklore and philosophical ideas.',
    skills: ['Video Production', 'Scriptwriting', 'Creative Writing', 'Art Direction', 'Mythology Research', 'Storytelling', 'Educational Content'],
    mediaGroups: [
      {
        label: 'Episodes',
        items: [
          { type: YT, url: 'https://youtu.be/PcfKA2A4Izc', label: 'The Cycle of Dashavatara' },
          { type: YT, url: 'https://youtu.be/dJu1jJ6uPno', label: 'Teachings of Gita' },
          { type: YT, url: 'https://youtu.be/f-sgl7ich5I', label: 'Weapons of Gods' },
          { type: YT, url: 'https://youtu.be/jTwd8Izldq4', label: 'Mythical Creatures from Indian Folklore' },
          { type: YT, url: 'https://youtu.be/2nru_rwbo8M', label: "Ravana's Metaphor" },
          { type: YT, url: 'https://youtu.be/Ha_Sj2EUAn4', label: 'Symbols of Divinity' },
          { type: YT, url: 'https://youtu.be/CVtPsV1oyTI', label: 'God as Zero' },
        ],
      },
    ],
  },
  {
    id: 'beingindian',
    cluster: 'ind',
    group: 'culturemachine',
    brand: 'Being Indian',
    date: 'Date not specified',
    location: 'India',
    role: 'Video Producer',
    description: null,
    skills: [],
    notes: ['Confirmed Culture Machine brand entry. Project details to be added.'],
  },
  {
    id: 'hilton-in',
    cluster: 'ind',
    group: 'mediacom',
    brand: 'Hilton Hotels',
    title: 'Goa Beyond the Beaches / #MyHiltonStory',
    date: 'During MediaCom tenure · May 2018 — Dec 2021',
    location: 'India',
    role: 'Content Lead and Creative Supervisor',
    agency: 'MediaCom India, GroupM, WPP Media',
    description:
      "Developed and led a 360-degree campaign promoting Hilton's river-facing Goa property and showcasing Goa's Portuguese heritage and cultural attractions beyond its beaches. Onboarded Condé Nast as a distribution partner with branded content across Condé Nast Traveller, Vogue, GQ and Architectural Digest, alongside print integration. Engaged Shibani Dandekar as the face of the brand film. The campaign spanned brand film, celebrity social amplification, Condé Nast branded content, YouTube pre-roll, Hotstar advertising, and print and digital magazine content. Also adapted regionally for Southeast Asia.",
    skills: ['Creative Strategy', 'Content Strategy', 'Creative Supervision', '360-Degree Campaign Development', 'Influencer Marketing', 'Branded Content', 'Media Partnerships', 'Multi-Platform Campaign Execution', 'Video Strategy', 'Regional Content Adaptation'],
    outcomes: ['Reported campaign reach: 25 million+ travellers', 'Won an Emvies award'],
    mediaGroups: [
      { label: 'Brand film', items: [{ type: YT, url: 'https://www.youtube.com/watch?v=18RqkAiOYbs' }] },
      {
        label: 'Publisher and brand posts',
        items: [
          { type: IG, url: 'https://www.instagram.com/p/B54Og4Sh8m5/', label: 'Vogue India brand film post' },
          { type: IG, url: 'https://www.instagram.com/p/B6DFCo-DeXn/', label: 'Brand repost' },
          { type: IG, url: 'https://www.instagram.com/p/B6M0swfB07v/', label: 'Static post' },
          { type: IG, url: 'https://www.instagram.com/p/B10KKK9gNjI/' },
          { type: IG, url: 'https://www.instagram.com/p/B7Gmvq_j2cB/' },
        ],
      },
      {
        label: 'Influencer collaboration',
        items: [
          { type: IG, url: 'https://www.instagram.com/p/B3GrAgXAcv0/' },
          { type: IG, url: 'https://www.instagram.com/p/B3OoB2hjYck/', label: 'Brand repost' },
        ],
      },
    ],
  },
  {
    id: 'vivo',
    cluster: 'ind',
    group: 'mediacom',
    brand: 'Vivo',
    title: 'X60 — Joy of Humanity',
    date: 'During MediaCom tenure · May 2018 — Dec 2021',
    location: 'India',
    role: 'Content Lead',
    description:
      "Developed a branded content campaign for Vivo's X60 flagship smartphone, focused on its mobile photography capabilities. Partnered with Better Photography magazine on the idea of capturing the Joy of Humanity, onboarding four professional photographers across wildlife, street, sports and portrait photography to demonstrate the camera's capabilities through authentic photography-led storytelling.",
    skills: ['Content Strategy', 'Content Leadership', 'Branded Content', 'Photography Campaign Development', 'Influencer and Photographer Partnerships', 'Product Storytelling', 'Creative Direction'],
    mediaGroups: [
      { label: 'Main campaign', items: [{ type: IG, url: 'https://www.instagram.com/p/CO-hK8VKHmH/' }] },
      { label: 'Wildlife', items: [{ type: IG, url: 'https://www.instagram.com/p/COiPHAeqyGs/' }, { type: IG, url: 'https://www.instagram.com/p/COfxk57qxBA/' }] },
      { label: 'Street', items: [{ type: IG, url: 'https://www.instagram.com/p/COQNyUQKBM6/' }, { type: IG, url: 'https://www.instagram.com/p/COX8Ak7qwW4/' }] },
      { label: 'Sports', items: [{ type: IG, url: 'https://www.instagram.com/p/CONxYZ1KefP/' }, { type: IG, url: 'https://www.instagram.com/p/CN5CYSkqXwr/' }] },
      { label: 'Portrait', items: [{ type: IG, url: 'https://www.instagram.com/p/COIfNuTqqWz/' }] },
    ],
  },
  {
    id: 'subway',
    cluster: 'ind',
    group: 'mediacom',
    brand: 'Subway',
    title: 'East Meets West',
    date: 'During MediaCom tenure · May 2018 — Dec 2021',
    location: 'India',
    role: 'Content Lead and Brand Liaison',
    description:
      "Developed a music-led campaign communicating Subway's refreshed brand identity and new fusion sandwich flavours to a younger audience. Identified music as a strong youth passion point and built a collaboration between Darshan Raval and composer Shore Police, bringing Indian classical and Western pop influences into a fusion music video inspired by the East Meets West proposition. Launched in stages: teaser content, the main fusion music video, and supporting social amplification.",
    skills: ['Creative Strategy', 'Content Strategy', 'Music-Led Campaign Development', 'Brand Partnerships', 'Artist Collaboration', 'Branded Entertainment', 'Video Production', 'Youth Marketing'],
    outcomes: ['The video reportedly crossed one million views in less than 24 hours'],
    mediaGroups: [
      {
        label: 'Campaign videos',
        items: [
          { type: YT, url: 'https://www.youtube.com/watch?v=fIlK82U_M_c' },
          { type: YT, url: 'https://www.youtube.com/watch?v=RZ77Ye4nycM' },
          { type: YT, url: 'https://www.youtube.com/watch?v=UCnTJ7B5mhY' },
          { type: YT, url: 'https://www.youtube.com/watch?v=7wN0RWUr3Vk' },
          { type: YT, url: 'https://www.youtube.com/watch?v=DvDb-XekTeo' },
        ],
      },
    ],
  },
  {
    id: 'shell',
    cluster: 'ind',
    group: 'mediacom',
    brand: 'Shell',
    title: 'PowerDrift Content Series',
    date: 'During MediaCom tenure · May 2018 — Dec 2021',
    location: 'India',
    role: 'Creative and Content Strategist — content strategy and campaign development',
    description:
      "Developed a branded content collaboration with PowerDrift to build awareness of Shell's fuel station services in India, communicating features such as Go Deli and rewards available through the Shell app. Worked on a series of automotive reviews that integrated Shell's services naturally into the content instead of presenting them as conventional advertisements.",
    skills: ['Branded Content Strategy', 'Automotive Content', 'Editorial Partnerships', 'Content Integration', 'Product Communication', 'Video Content Development'],
    mediaGroups: [
      {
        label: 'Content series',
        items: [
          { type: YT, url: 'https://www.youtube.com/watch?v=yfp6aTZ8vRY' },
          { type: YT, url: 'https://www.youtube.com/watch?v=Ik8J5aZGyw4' },
          { type: YT, url: 'https://youtu.be/LDIOju8NjKU' },
        ],
      },
    ],
  },
  {
    id: 'bosch',
    cluster: 'ind',
    group: 'mediacom',
    brand: 'Bosch South India',
    date: 'During MediaCom tenure · May 2018 — Dec 2021',
    location: 'India',
    role: 'Creative and Content Strategist, MediaCom',
    description: null,
    skills: ['Creative Strategy', 'Content Strategy'],
    notes: ['Campaign links will be supplied as part of the project media assets. Brand and role shown until then.'],
  },
  {
    id: 'porsche',
    cluster: 'ind',
    group: 'mediacom',
    brand: 'Porsche India',
    title: 'Owners Roadshow',
    date: 'During MediaCom tenure · May 2018 — Dec 2021',
    location: 'India',
    role: 'Production Supervisor and Content Direction',
    description:
      "Worked on a campaign celebrating Porsche's legacy, inviting Porsche owners in India to an exclusive driving experience at Buddh International Circuit featuring new Porsche 911 models, designed to build aspiration around the brand. Supervised production and provided content direction for cinematic event coverage for Porsche India's social channels.",
    skills: ['Production Supervision', 'Creative Direction', 'Event Content', 'Video Production', 'Automotive Storytelling', 'Social Media Content'],
    outcomes: ['Reported increase in followers and a surge in vehicle bookings'],
    mediaGroups: [
      {
        label: 'Event coverage',
        items: [
          { type: IG, url: 'https://www.instagram.com/p/BwluAe9HfPu/' },
          { type: IG, url: 'https://www.instagram.com/p/BwrYC0YHVs5/' },
          { type: IG, url: 'https://www.instagram.com/p/BwjSeH4Hr6E/' },
        ],
      },
    ],
  },
  {
    id: 'dronestark',
    cluster: 'ind',
    group: 'independent',
    brand: 'Dronestark',
    date: 'Date not specified',
    location: 'India',
    role: 'Independent Creative Consultant',
    description: null,
    skills: ['Brand Strategy', 'Visual Identity', 'Logo and Brand Guidelines', 'Content Strategy', 'Social Media Strategy', 'Company Profile and Sales Communication'],
    notes: ['Some work is covered by an NDA and is not published.'],
  },
  {
    id: 'mimamsa',
    cluster: 'ind',
    group: 'independent',
    brand: 'Mimamsa',
    date: 'Date not specified',
    location: 'India',
    role: 'Independent Creative Consultant',
    description: null,
    skills: ['Personal Branding', 'Integrated Marketing Strategy', 'Website Strategy', 'Social Media Strategy', 'LinkedIn Outreach', 'Workshop Acquisition Strategy', 'Performance Marketing'],
    notes: ['Some examples are not approved for public sharing and are not published.'],
  },
  {
    id: 'comfort',
    cluster: 'ind',
    group: 'hul',
    brand: 'Comfort Fabric Conditioners, Unilever',
    date: 'Oct 2024 — Apr 2025 (HUL tenure)',
    location: 'India',
    role: 'Social Lead and Content Strategist',
    description:
      'Led the social media strategy, content and creative direction for a flagship video campaign supporting the relaunch of Comfort Fabric Conditioners, centred on newly appointed brand ambassador Rashmika Mandanna. Developed a full-funnel, always-on content strategy focused on freshness, fragrance and premium lifestyle storytelling with a Gen Z-first brand voice, platform-first formats, high-performing Reels, cultural moments and AI-enhanced creative workflows. Introduced an ambassador-led content series and led the visual shift from static creatives to premium studio-quality content. FMCG / CPG branded content using AI-assisted and live-action production.',
    skills: ['Social Media Strategy', 'Content Strategy', 'Creative Direction', 'Brand Communication', 'AI-Assisted Creative Workflows', 'Content Production', 'Audience-Focused Storytelling'],
    outcomes: ['533% increase in engagement', 'Doubled the follower base organically'],
    mediaGroups: [
      { label: 'Campaign films', items: [{ type: YT, url: 'https://www.youtube.com/watch?v=FIJX5wy3r9k' }, { type: YT, url: 'https://www.youtube.com/watch?v=s6PUnD_6iHA' }] },
      { label: 'Campaign content', items: [{ type: IG, url: 'https://www.instagram.com/p/DH5V0QHMltI/' }, { type: IG, url: 'https://www.instagram.com/p/DG0An4nSu-f/' }, { type: IG, url: 'https://www.instagram.com/p/DIJHwX7ocjC/' }] },
    ],
  },
  {
    id: 'surfexcel',
    cluster: 'ind',
    group: 'hul',
    brand: 'Surf Excel, Unilever',
    date: 'Oct 2024 — Apr 2025 (HUL tenure)',
    location: 'India',
    role: 'Social Media Lead',
    description: null,
    skills: ['Social Media Strategy'],
    notes: ['Confirmed brand entry. Project details to be added.'],
  },

  // ─── MARKET 04 — SOUTHEAST ASIA ──────────────────────────────
  {
    id: 'hilton-sea',
    cluster: 'sea',
    brand: 'Hilton Hotels',
    title: 'Goa Beyond the Beaches — Regional Adaptation',
    date: 'Date not specified',
    location: 'Singapore & Indonesia',
    role: 'Creative Lead',
    description:
      'Regional adaptation of the original India campaign, Goa Beyond the Beaches / #MyHiltonStory. The campaign originated in India and was adapted for Singapore and Indonesia — the same campaign, taken across borders. See the India entry for the original campaign record and full media.',
    skills: ['Creative Strategy', 'Content Adaptation', 'Campaign Supervision', 'Regional Campaign Execution', 'Multi-Market Content Strategy'],
    mediaGroups: [{ label: 'Original brand film', items: [{ type: YT, url: 'https://www.youtube.com/watch?v=18RqkAiOYbs' }] }],
  },
];

export const MARKET_SECTIONS = [
  { id: 'na', num: '01', name: 'North America', heading: 'CANADA · UNITED STATES' },
  { id: 'uae', num: '02', name: 'UAE', heading: 'DUBAI, UAE' },
  { id: 'ind', num: '03', name: 'India', heading: 'INDIA' },
  { id: 'sea', num: '04', name: 'Southeast Asia', heading: 'SINGAPORE · INDONESIA' },
];

export function youtubeId(url) {
  const m = url.match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}
